import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { autobind } from "core-decorators";
import { MapboxCustomObject } from "./MapboxCustomObject";
import type { FeatureMeasureLengthData } from "@/types/content";
import * as mapboxgl from "mapbox-gl";
import { calculateLength } from "@/utils/geometry-utils";

export class MapboxMeasureLengthObject extends MapboxCustomObject {
  constructor(mapboxViewer: MapboxViewer, content: FeatureMeasureLengthData) {
    super(mapboxViewer, content);
    this._contentId = content.id;
    this._editor = this._mapboxViewer.editors.editorMeasureLengthController!;
    this.init(content);
  }

  @autobind
  init(content: FeatureMeasureLengthData) {
    this._content = content;
    this._contentId = content.id;
    // Add to shared entity
    this._mapboxViewer.entities.measureLengthEntity?.add(this);
  }

  set visible(visible: boolean) {
    this._content.config.visible = visible;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  get visible() {
    return this._content.config.visible;
  }

  @autobind
  getFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length < 2) {
      return null;
    }

    const content = this._content as FeatureMeasureLengthData;

    const feature: GeoJSON.Feature = {
      type: "Feature",
      id: this._contentId,
      geometry: {
        type: "LineString",
        coordinates: this._content.coordinates,
      },
      properties: {
        contentId: this._contentId,
        app: "logistic-app",
        color: content.config.color || "#ff6b6b",
        name: `Length: ${this.formatLength(content.properties.length)}`,
        category: "measurement_length",
        length: content.properties.length,
        unit: content.properties.unit,
        visible: content.config.visible,
      },
    };

    return feature;
  }

  @autobind
  getLabelFeatures(): GeoJSON.Feature[] {
    if (!this._content.coordinates || this._content.coordinates.length < 2) {
      return [];
    }

    const content = this._content as FeatureMeasureLengthData;
    const features: GeoJSON.Feature[] = [];
    const coordinates = this._content.coordinates;

    // Add vertex points for each corner
    for (let i = 0; i < coordinates.length; i++) {
      const coord = coordinates[i];
      features.push({
        type: "Feature",
        id: `${this._contentId}-vertex-${i}`,
        geometry: {
          type: "Point",
          coordinates: coord,
        },
        properties: {
          app: "logistic-app",
          measureId: this._contentId,
          vertexType: "corner",
          vertexIndex: i,
          category: "measure_label",
          visible: content.config.visible,
        },
      });
    }

    // Add midpoint vertices for editing
    for (let i = 0; i < coordinates.length - 1; i++) {
      const current = coordinates[i];
      const next = coordinates[i + 1];
      const midpoint = [(current[0] + next[0]) / 2, (current[1] + next[1]) / 2];

      features.push({
        type: "Feature",
        id: `${this._contentId}-midpoint-${i}`,
        geometry: {
          type: "Point",
          coordinates: midpoint,
        },
        properties: {
          app: "logistic-app",
          measureId: this._contentId,
          vertexType: "midpoint",
          insertIndex: i + 1,
          category: "measure_label",
          visible: content.config.visible,
        },
      });
    }

    // Add labels at each segment midpoint
    for (let i = 0; i < coordinates.length - 1; i++) {
      const start = coordinates[i];
      const end = coordinates[i + 1];
      const midpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];

      const segmentLength = calculateLength([start, end] as [number, number][]);

      features.push({
        type: "Feature",
        id: `${this._contentId}-label-${i}`,
        geometry: {
          type: "Point",
          coordinates: midpoint,
        },
        properties: {
          app: "logistic-app",
          measureId: this._contentId,
          labelType: "segment_length",
          segmentIndex: i,
          length: segmentLength,
          lengthFormatted: this.formatLength(segmentLength),
          unit: content.properties.unit,
          category: "measure_label",
          visible: content.config.visible,
        },
      });
    }

    // Add total length label at the middle of the entire line
    // if (coordinates.length > 1) {
    //   const totalMidpoint = this.calculateTotalMidpoint(coordinates);
    //   features.push({
    //     type: "Feature",
    //     id: `${this._contentId}-total-label`,
    //     geometry: {
    //       type: "Point",
    //       coordinates: totalMidpoint,
    //     },
    //     properties: {
    //       app: "logistic-app",
    //       measureId: this._contentId,
    //       labelType: "total_length",
    //       length: content.properties.length,
    //       lengthFormatted: this.formatLength(content.properties.length),
    //       unit: content.properties.unit,
    //       category: "measure_label",
    //       visible: content.config.visible,
    //     },
    //   });
    // }

    return features;
  }

  @autobind
  updateContent(content: FeatureMeasureLengthData) {
    this._content = content;
    this._mapboxViewer.entities.measureLengthEntity?.updateSource();
  }

  @autobind
  dispose() {
    this._mapboxViewer.entities.measureLengthEntity?.remove(this._contentId);
  }

  @autobind
  setEditing(editing: boolean) {
    // Update editing state and trigger source update
    if (editing) {
      // Enable editing mode - show vertices
      this._mapboxViewer.entities.measureLengthEntity?.showVertices();
    } else {
      // Disable editing mode - hide vertices
      this._mapboxViewer.entities.measureLengthEntity?.hideVertices();
    }
    this._mapboxViewer.entities.measureLengthEntity?.updateSource();
  }

  @autobind
  setDragHighlight(highlighted: boolean) {
    // Update feature state for visual feedback
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: "measure-source", id: this._contentId },
      { dragging: highlighted }
    );
  }

  @autobind
  moveMeasure(newCoordinates: number[][]) {
    // Temporarily update coordinates for real-time feedback
    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureLengthEntity?.updateSource();
  }

  @autobind
  updateVertex(vertexIndex: number, newCoordinate: number[]) {
    if (
      !this._content.coordinates ||
      vertexIndex < 0 ||
      vertexIndex >= this._content.coordinates.length
    ) {
      return;
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates[vertexIndex] = newCoordinate;

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureLengthEntity?.updateSource();
  }

  @autobind
  insertVertex(insertIndex: number, newCoordinate: number[]) {
    if (
      !this._content.coordinates ||
      insertIndex < 0 ||
      insertIndex > this._content.coordinates.length
    ) {
      return;
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates.splice(insertIndex, 0, newCoordinate);

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureLengthEntity?.updateSource();
  }

  @autobind
  removeVertex(vertexIndex: number) {
    if (
      !this._content.coordinates ||
      this._content.coordinates.length <= 2 ||
      vertexIndex < 0 ||
      vertexIndex >= this._content.coordinates.length
    ) {
      return; // Don't allow removal if it would make an invalid line
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates.splice(vertexIndex, 1);

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureLengthEntity?.updateSource();
  }

  private formatLength(length: number): string {
    return `${length.toFixed(2)} m`;
  }

  //   private calculateTotalMidpoint(coordinates: number[][]): number[] {
  //     let totalX = 0;
  //     let totalY = 0;
  //     const n = coordinates.length;

  //     for (const coord of coordinates) {
  //       totalX += coord[0];
  //       totalY += coord[1];
  //     }

  //     return [totalX / n, totalY / n];
  //   }

  @autobind
  focusOn() {
    const coordinates = this._content.coordinates;
    if (!coordinates || coordinates.length === 0) return;

    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord as mapboxgl.LngLatLike);
    }, new mapboxgl.LngLatBounds());
    this._mapboxViewer._mapViewer.fitBounds(bounds);
  }

  @autobind
  setHighlight() {
    // this._mapboxViewer._mapViewer.setFeatureState(
    //   { source: "measure-source", id: this._contentId },
    //   { dragging: true }
    // );
  }
}
